import React, { useState } from 'react';
import Layout from 'layouts/manager/Layout';
import Pagination from 'components/Pagination';
import { Search as SearchIcon } from 'lucide-react';
import Table, { TableColumn } from 'components/Table';

// ダミーデータ
const dummyMembers = Array.from({ length: 23 }).map((_, i) => ({
  id: i + 1,
  loginId: `login${i + 1}`,
  memberNo: `M${1000 + i}`,
  storeName: `店舗${i + 1}`,
  managerName: `担当${i + 1}`,
  tel: `03-0000-00${('0' + (i + 1)).slice(-2)}`,
  email: `user${i + 1}@example.com`,
}));

const PAGE_SIZE = 10;

const columns: TableColumn<typeof dummyMembers[0]>[] = [
  { key: 'loginId', label: 'ログインID' },
  { key: 'memberNo', label: '会員番号' },
  { key: 'storeName', label: '店舗名' },
  { key: 'managerName', label: '担当者名' },
  { key: 'tel', label: '電話番号' },
  { key: 'email', label: 'メールアドレス' },
];

const MemberLink: React.FC = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [checked, setChecked] = useState<number[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<string>('');

  // 検索・ページネーション処理
  const filtered = dummyMembers.filter(m =>
    m.loginId.includes(search) ||
    m.memberNo.includes(search) ||
    m.storeName.includes(search) ||
    m.managerName.includes(search) ||
    m.tel.includes(search) ||
    m.email.includes(search)
  );
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);

  // チェックボックス処理
  const toggleCheck = (id: number) => {
    setChecked(checked => checked.includes(id) ? checked.filter(c => c !== id) : [...checked, id]);
  };
  const allChecked = paged.every(m => checked.includes(m.id));
  const toggleAll = () => {
    if (allChecked) setChecked(checked.filter(id => !paged.some(m => m.id === id)));
    else setChecked([...checked, ...paged.filter(m => !checked.includes(m.id)).map(m => m.id)]);
  };

  // CSVプレビュー
  const handleCsvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setCsvFile(file || null);
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => setCsvPreview(ev.target?.result as string || '');
      reader.readAsText(file);
    } else {
      setCsvPreview('');
    }
  };

  // csvFileのダミー利用（実際のアップロード処理が未実装の場合の警告表示など）
  React.useEffect(() => {
    if (csvFile) {
      // ダミーでファイル名をアラート表示
      // 実際はアップロード処理やバリデーション等をここで実装
      alert(`選択されたCSVファイル: ${csvFile.name}`);
    }
  }, [csvFile]);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-800">店舗紐付け</h1>
      </div>
      <div className="mb-4">
        <a href="/manager/login-id" className="text-sm text-gray-500 underline transition hover:text-gray-700">&lt; 一覧画面に戻る</a>
      </div>
      {/* テーブル（Search.tsxと同じデザイン） */}
      <div className="p-4 bg-white border rounded shadow">
        {/* 上部操作エリア（カード内に移動） */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex gap-2">
            <button
              type="button"
              className="px-3 py-1 text-xs font-semibold rounded"
              style={{ background: '#002c6a', color: '#fff' }}
              onClick={() => setShowAddModal(true)}
            >
              会員追加
            </button>
            <button
              className="px-3 py-1 text-xs font-semibold border border-blue-900 rounded"
              style={{ color: '#002c6a', background: '#f7fafd' }}
              type="button"
              onClick={() => setShowCsvModal(true)}
            >
              CSVインポート
            </button>
          </div>
          <div className="relative w-64">
            <input
              type="text"
              className="w-full py-2 pl-10 pr-3 text-sm text-gray-700 placeholder-gray-500 border rounded focus:outline-none focus:ring-2 focus:ring-blue-300"
              placeholder="フリーワード検索"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
            <span className="absolute text-blue-700 -translate-y-1/2 pointer-events-none left-3 top-1/2">
              <SearchIcon size={18} />
            </span>
          </div>
        </div>
        <Table
          data={paged}
          columns={columns}
          checked={checked}
          onCheck={id => toggleCheck(id)}
          onCheckAll={toggleAll}
          showCheckbox
          actions={[
            {
              label: '削除',
              color: 'text-red-500',
              hover: 'hover:underline',
              onClick: (row) => { alert(`削除: ${row.loginId}`); },
            },
          ]}
        />
        {/* ページネーション（カード内に移動） */}
        <Pagination page={page} totalPages={pageCount} onPageChange={setPage} />
      </div>
      {/* 会員追加モーダル */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="relative flex flex-col bg-white rounded-lg shadow-lg" style={{ width: 600, height: 400 }}>
            <button
              className="absolute flex items-center justify-center w-8 h-8 text-xl text-gray-500 transition rounded-full top-3 right-3 hover:text-gray-700 focus:outline-none"
              style={{ fontSize: 20, padding: 0 }}
              onClick={() => setShowAddModal(false)}
              aria-label="閉じる"
            >
              ×
            </button>
            {/* モーダル内容をスクロール可能に */}
            <div className="p-6 overflow-y-auto" style={{ flex: 1, minHeight: 0 }}>
              <h2 className="mb-4 text-lg font-bold">会員追加</h2>
              <div className="flex items-center justify-between mb-4">
                <input type="text" className="w-64 px-3 py-2 text-sm border rounded" placeholder="フリーワード検索" />
                <button className="px-4 py-2 text-sm font-semibold text-white rounded" style={{ background: '#002c6a' }}>検索</button>
              </div>
              <div className="mb-4 overflow-x-auto" style={{ maxHeight: 180 }}>
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2"><input type="checkbox" /></th>
                      <th className="p-2">ログインID</th>
                      <th className="p-2">会員番号</th>
                      <th className="p-2">店舗名</th>
                      <th className="p-2">担当者名</th>
                      <th className="p-2">電話番号</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* ダミー検索結果 */}
                    <tr>
                      <td className="p-2 text-center"><input type="checkbox" /></td>
                      <td className="p-2">login99</td>
                      <td className="p-2">M9999</td>
                      <td className="p-2">店舗99</td>
                      <td className="p-2">担当99</td>
                      <td className="p-2">03-9999-9999</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            {/* ボタンを右下に固定配置 */}
            <div className="sticky bottom-0 flex justify-end gap-2 p-4 bg-white border-t rounded-b-lg">
              <button className="px-4 py-2 text-sm font-semibold text-white rounded" style={{ background: '#002c6a' }}>追加</button>
              <button className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setShowAddModal(false)}>キャンセル</button>
            </div>
          </div>
        </div>
      )}

      {/* CSVインポートモーダル */}
      {showCsvModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="relative bg-white rounded-lg shadow-lg" style={{ width: 600, height: 400 }}>
            <button
              className="absolute flex items-center justify-center w-8 h-8 text-xl text-gray-500 transition rounded-full top-3 right-3 hover:text-gray-700 focus:outline-none"
              style={{ fontSize: 20, padding: 0 }}
              onClick={() => setShowCsvModal(false)}
              aria-label="閉じる"
            >
              ×
            </button>
            <div className="p-6">
              <h2 className="mb-4 text-lg font-bold">CSVインポート</h2>
              <div className="mb-4">
                <input type="file" accept=".csv" onChange={handleCsvChange} />
                <button className="px-4 py-2 ml-2 text-sm font-semibold text-white bg-green-600 rounded hover:bg-green-700">アップロード</button>
                <button className="px-4 py-2 ml-2 text-sm font-semibold text-gray-700 bg-gray-200 rounded hover:bg-gray-300" onClick={() => setShowCsvModal(false)}>キャンセル</button>
              </div>
              <div className="h-40 p-2 overflow-auto border rounded bg-gray-50">
                <pre className="text-xs whitespace-pre-wrap">{csvPreview || 'CSVファイルのプレビューがここに表示されます'}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default MemberLink;
